import ChevronUpRoundedIcon from '@assets/icons/chevron-up-rounded.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import { AlertCard } from './alert-card';
import { createStyles } from '@/contexts/theme-context';
import { FlatList, StyleProp, ViewStyle } from 'react-native';
import { HubCardData } from '@/hooks/insights-hub/types';
import { HubCardType } from '@/types';
import { Indicator } from './indicator';
import { ListItemSeparator } from '@/components/list-item';
import { shadow } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';

export interface Props {
  alerts: HubCardData[];
  containerStyle?: StyleProp<ViewStyle>;
  ItemSeparatorComponent?: React.ComponentType;
  onPress: (cardData: HubCardData) => void;
  onPressSupportInfo: (cardData: HubCardData) => void;
  toggleGroup?: (alertCount: number, cardType: HubCardType) => void;
}

export const HubFeedCardsList: React.FC<Props> = ({
  alerts,
  containerStyle,
  ItemSeparatorComponent,
  onPress,
  onPressSupportInfo,
  toggleGroup,
}) => {
  const [styles] = useStyles();
  const { isSmallLayout } = useLayoutContext();

  // alerts is an array of alerts, so alertCount is the number of alerts - the top alert, which is 1 less than the length
  const alertCount = alerts.length - 1;

  return (
    <FlatList
      data={alerts}
      ItemSeparatorComponent={ItemSeparatorComponent}
      keyExtractor={(alert) => alert.alert_uuid}
      ListFooterComponent={
        toggleGroup ? (
          <>
            <Indicator
              accessibilityLabel={i18n.t('collapse')}
              Icon={ChevronUpRoundedIcon}
              onPress={() => toggleGroup(alertCount, alerts[0].card_type)}
              style={[
                styles.indicator,
                isSmallLayout
                  ? styles.indicatorMobileMargin
                  : styles.indicatorDesktopBackground,
              ]}
            />
            {!isSmallLayout ? (
              <ListItemSeparator style={styles.separator} />
            ) : null}
          </>
        ) : null
      }
      renderItem={({ item: alert, index }) => {
        const isFirstItem = alerts[0].alert_uuid === alert.alert_uuid;
        const isLastItem =
          alerts[alerts.length - 1].alert_uuid === alert.alert_uuid;
        return (
          <AlertCard
            containerStyle={[
              isFirstItem ? styles.cardTopBorderRadius : null,
              isLastItem ? styles.cardBottomBorderRadius : null,
              containerStyle,
            ]}
            index={index}
            item={alert}
            onPress={() => onPress(alert)}
            onPressSupportInfo={() => onPressSupportInfo(alert)}
          />
        );
      }}
      scrollEnabled={false}
      showsVerticalScrollIndicator={false}
    />
  );
};

const useStyles = createStyles((theme) => ({
  cardBottomBorderRadius: {
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  cardTopBorderRadius: {
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  indicator: {
    backgroundColor: theme.sk.color.bg.primary.default,
    height: 24,
    marginBottom: 8,
    width: 32,
  },
  indicatorDesktopBackground: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
  },
  indicatorMobileMargin: {
    marginBottom: 16,
    marginTop: 8,
    ...shadow({
      elevation: 2,
      opacity: 0.1,
      radius: 2,
      x: 0,
      y: 2,
    }),
  },
  separator: {
    borderBottomColor: theme.sk.color.divider.default,
    borderBottomWidth: 1,
    marginHorizontal: 0,
  },
}));
